import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Radio } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${BACKEND_URL}/api`;

/**
 * Consumes a one-time device-link token from `?token=` and signs the visitor
 * in as the token's issuer. Used by the QR code on the Landing page so a DJ
 * can beam their session to a Smart TV / phone / second laptop by scanning
 * a code — no password re-entry.
 */
export default function DevicePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "linking" : "missing");
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();
  const { setAuthFromReset } = useAuth();

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/device-link/consume`, { token });
        if (cancelled) return;
        if (r.data.token && setAuthFromReset) setAuthFromReset(r.data.token, r.data.user);
        setStatus("linked");
        setTimeout(() => navigate("/studio"), 1200);
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err.response?.data?.detail || "Device link failed. Ask the DJ for a fresh QR code.");
        setStatus("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate, setAuthFromReset]);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center px-6" data-testid="device-link-page">
      <div className="w-full max-w-md rounded-2xl border border-[#2A354B] bg-[#121620] p-8 space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF] flex items-center justify-center">
            <Radio className="w-5 h-5 text-[#00F0FF]"/>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">// Device link</div>
            <div className="font-display font-black text-2xl">Linking this device…</div>
          </div>
        </div>

        {status === "linking" && (
          <div className="flex items-center gap-3 text-sm text-[#94A3B8]" data-testid="device-link-status-linking">
            <Loader2 className="w-4 h-4 animate-spin"/> Exchanging your QR code for a session…
          </div>
        )}

        {status === "linked" && (
          <div className="p-4 rounded border border-[#10B981]/50 bg-[#10B981]/10 text-sm text-[#F8FAFC]" data-testid="device-link-status-linked">
            You're in. Opening the studio…
          </div>
        )}

        {status === "missing" && (
          <div className="p-4 rounded border border-[#EF4444]/50 bg-[#EF4444]/10 text-sm text-[#F8FAFC]" data-testid="device-link-status-missing">
            No token in this link. Scan a fresh QR from the landing page.
          </div>
        )}

        {status === "error" && (
          <div className="space-y-3" data-testid="device-link-status-error">
            <div className="p-4 rounded border border-[#EF4444]/50 bg-[#EF4444]/10 text-sm text-[#F8FAFC]">
              {errorMsg}
            </div>
            <Link to="/" className="block text-center text-xs font-mono uppercase tracking-widest text-[#00F0FF]">← Back to home</Link>
          </div>
        )}
      </div>
    </div>
  );
}
